import React, { useState, useEffect } from 'react';
import * as storage from './utils/storage';
import { getLevelInfo, hotScore } from './utils/scoring';
import { useStreak } from './hooks/useStreak';
import { useChallenges } from './hooks/useChallenges';
import { StreakDeath } from './components/overlays/StreakDeath';
import { UndoBanner } from './components/overlays/UndoBanner';
import { InstantShare } from './components/overlays/InstantShare';
import { OnboardingModal } from './components/onboarding/OnboardingModal';
import { LevelUpEffect } from './components/overlays/LevelUpEffect';
import { ExecuteMode } from './components/execute/ExecuteMode';
import { ProfileView } from './components/profile/ProfileView';
import { UsernameSetup } from './components/onboarding/UsernameSetup';
import { AuthScreen } from './components/auth/AuthScreen';
import { ViewProfileModal } from './components/community/ViewProfileModal';
import { LogView } from './components/log/LogView';
import { ShareSuccessToast } from './components/overlays/ShareSuccessToast';
import { StreakSafeBanner } from './components/overlays/StreakSafeBanner';
import { StreakWarningBanner } from './components/overlays/StreakWarningBanner';
import { PWAInstallPrompt } from './components/overlays/PWAInstallPrompt';
import { BadgeUnlockEffect } from './components/overlays/BadgeUnlockEffect';
import { CelebrationEffect } from './components/overlays/CelebrationEffect';
import { Header } from './components/shared/Header';
import { NavBar } from './components/shared/NavBar';
import { PremiumUpgradeModal } from './components/shared/PremiumUpgradeModal';
import { CommunityView } from './components/community/CommunityView';
import { HomeView } from './components/challenges/HomeView';
import { PactView } from './components/pact/PactView';
import * as labService from './services/labService';
import * as community from './communityService';
import { supabase } from './supabaseClient'
import { migrateIfNeeded } from './utils/migrate';
import { loadUserStats, loadLogs, loadDecisions, loadChallengeState, loadAndClearCustomChallenge, updateUserStats, saveLogs, saveDecisions, saveChallengeState } from './utils/userService';
import { linkAnonymousAccount } from './utils/authService';
import { hardModeChallenges } from './data/challenges';

const SendItMaxxApp = () => {
  const [currentView, setCurrentView] = useState('home');
  const [sendItScore, setSendItScore] = useState(0);
  const [decisionText, setDecisionText] = useState('');
  const [coinFlipMode, setCoinFlipMode] = useState(false);
  const [optionA, setOptionA] = useState('');
  const [optionB, setOptionB] = useState('');
  const [coinResult, setCoinResult] = useState(null);
  const [savedDecisions, setSavedDecisions] = useState([]);
  const [countdown, setCountdown] = useState(null);
  const [countdownUses, setCountdownUses] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [currentDecision, setCurrentDecision] = useState(null);
  const [logs, setLogs] = useState([]);
  const [pendingOutcomes, setPendingOutcomes] = useState([]);
  const [communityFeed, setCommunityFeed] = useState([]);
  const [shareText, setShareText] = useState('');
  const [shareCategory, setShareCategory] = useState('social');
  const [shareVibe, setShareVibe] = useState('lockedin');
  const [wouldSendAgain, setWouldSendAgain] = useState(true);
  const [userId, setUserId] = useState(null);
  const [hasCloudSession, setHasCloudSession] = useState(false);
  const [isAnonymousUser, setIsAnonymousUser] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authEmailConfirmed, setAuthEmailConfirmed] = useState(false);
  const [authGateMode, setAuthGateMode] = useState(() =>
    new URLSearchParams(window.location.search).get('auth') === 'recovery'
      ? 'update_password'
      : 'checking'
  );
  const [lastCompletionAt, setLastCompletionAt] = useState(() => storage.getLastCompletion());
  const [userVotes, setUserVotes] = useState({});
  const [feedFilter, setFeedFilter] = useState('recent'); // 'top', 'recent', 'category'
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showSendAgainOnly, setShowSendAgainOnly] = useState(false);
  const [isLoadingFeed, setIsLoadingFeed] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const [postError, setPostError] = useState(null);
  const MAX_POST_LENGTH = 280;
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [showConfetti, setShowConfetti] = useState(false);
  const [celebrationType, setCelebrationType] = useState(null);
  const [pulseScore, setPulseScore] = useState(false);
  const [shakeDaily, setShakeDaily] = useState(false);
  const [shakeWeekly, setShakeWeekly] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpData, setLevelUpData] = useState(null);
  const [previousLevel, setPreviousLevel] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const [undoCountdown, setUndoCountdown] = useState(2);
  const [undoAction, setUndoAction] = useState(null);
  const [showInstantShare, setShowInstantShare] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [activeCommentPost, setActiveCommentPost] = useState(null);
  const [comments, setComments] = useState({});
  const [lastCommentTime, setLastCommentTime] = useState(0);
  const [lastCommentText, setLastCommentText] = useState('');
  const [commentCooldown, setCommentCooldown] = useState(0);
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [inputError, setInputError] = useState('');

  // Share to social media function
  const shareToSocial = (type, data) => {
    let shareText = '';

    if (type === 'challenge') {
      shareText = `🔥 SENT IT: "${data.challenge}"\n\nJoin me on SendItMaxx - Stop overthinking, start doing.\n#SendItMaxx #JustSendIt`;
    } else if (type === 'profile') {
      shareText = `💪 My SendItMaxx Stats:\n🔥 ${streak} day streak\n⚡ ${sendItScore} points\n🏆 ${getUserStats().totalSends} sends\n\nStop overthinking. Start sending.\n#SendItMaxx`;
    } else if (type === 'post') {
      shareText = `"${data.text}"\n\n- Shared from SendItMaxx\n#SendItMaxx #JustSendIt`;
    }

    // Try native share API first (mobile)
    if (navigator.share) {
      navigator.share({
        text: shareText,
        url: window.location.href
      }).then(() => {
        setShowShareSuccess(true);
        setTimeout(() => setShowShareSuccess(false), 3000);
      }).catch(err => {
        // User cancelled or error - fallback to clipboard
        if (err.name !== 'AbortError') {
          copyToClipboard(shareText);
        }
      });
    } else {
      // Desktop - copy to clipboard
      copyToClipboard(shareText);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setShowShareSuccess(true);
      setTimeout(() => setShowShareSuccess(false), 3000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    });
  };
  const [showUsernameSetup, setShowUsernameSetup] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [bioInput, setBioInput] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [lastUsernameChange, setLastUsernameChange] = useState(null);
  const [badges, setBadges] = useState([]);
  const [showBadgeUnlock, setShowBadgeUnlock] = useState(false);
  const [newlyUnlockedBadge, setNewlyUnlockedBadge] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingCard, setOnboardingCard] = useState(1);
  const [showShareSuccess, setShowShareSuccess] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  const [pactNotification, setPactNotification] = useState(false);

  // Challenge Lab state
  const [challengeSubView,   setChallengeSubView]   = useState('challenges'); // 'challenges' | 'lab'
  const [promotedChallenges, setPromotedChallenges] = useState([]);
  const [isPremium,          setIsPremium]          = useState(false);

  // Swap It state
  const [swapUsed, setSwapUsed] = useState(() => storage.isSwapUsedThisWeek());

  // Premium upgrade modal
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [premiumAccountPrompt, setPremiumAccountPrompt] = useState(false);
  const openUpgradeModal = () => {
    if (!hasCloudSession || isAnonymousUser || !authEmailConfirmed) {
      setPremiumAccountPrompt(true);
      setCurrentView('profile');
      return;
    }
    setPremiumAccountPrompt(false);
    setShowUpgradeModal(true);
  };

  // Hard Mode
  const [hardModeActive, setHardModeActive] = useState(false);
  const [hardModeChallenge, setHardModeChallenge] = useState(null);

  // Pact invite link
  const [pendingPactCode, setPendingPactCode] = useState(null);

  // Dev mode detection
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const {
    streak, setStreak,
    longestStreak, setLongestStreak,
    recoveryMode, setRecoveryMode,
    showStreakWarning, setShowStreakWarning,
    showStreakDeath, setShowStreakDeath,
    showStreakSafe, setShowStreakSafe,
    getStreakWarningStatus,
  } = useStreak(isDataLoaded);

  const {
    dailyChallenge, setDailyChallenge,
    weeklyChallenge, setWeeklyChallenge,
    weeklyCompleted, setWeeklyCompleted,
    weeklyStartDate, setWeeklyStartDate,
    daysUntilNextWeekly, setDaysUntilNextWeekly,
    dailyChallengeCount, setDailyChallengeCount,
    weeklyChallengeCount, setWeeklyChallengeCount,
    rerollTokens, setRerollTokens,
    maxRerollTokens,
    showRerollAnimation, setShowRerollAnimation,
    generateNewDailyChallenge,
    generateNewWeeklyChallenge,
    loadChallengeCompletionCount,
    incrementChallengeCount,
    getTimeRemaining,
  } = useChallenges({ logs, setLogs, currentTime });

  const [communitySubView, setCommunitySubView] = useState('feed'); // 'feed', 'leaderboard', 'post'
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [currentSettingsTab, setCurrentSettingsTab] = useState('general'); // 'general', 'account'

  // Number counting animation
  const useCountUp = (end, duration = 1000) => {
    const [count, setCount] = useState(end);
    const [prevEnd, setPrevEnd] = useState(end);

    useEffect(() => {
      if (end === prevEnd) return;

      const start = prevEnd;
      const increment = end - start;
      const stepTime = Math.abs(Math.floor(duration / increment));

      let current = start;
      const timer = setInterval(() => {
        current += increment > 0 ? 1 : -1;
        if ((increment > 0 && current >= end) || (increment < 0 && current <= end)) {
          setCount(end);
          setPrevEnd(end);
          clearInterval(timer);
        } else {
          setCount(current);
        }
      }, stepTime);

      return () => clearInterval(timer);
    }, [end, prevEnd, duration]);

    return count;
  };

  const animatedStreak = useCountUp(streak, 800);
  const animatedScore = useCountUp(sendItScore, 1000);
  const animatedRerolls = useCountUp(rerollTokens, 500);

  // Notification functions
const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    alert('This browser does not support notifications');
    return;
  }

  try {
    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    
    if (permission === 'granted') {
      setNotificationsEnabled(true);
      // Pass true directly — don't rely on notificationsEnabled state
      sendNotificationDirect('🔥 Notifications Enabled!', "You'll get reminders to keep your streak alive");
    } else if (permission === 'denied') {
      setNotificationsEnabled(false);
      alert('Notifications blocked. You can enable them later in your browser settings.');
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
  }
};

// New direct version that doesn't check notificationsEnabled state
const sendNotificationDirect = (title, body, options = {}) => {
  if (Notification.permission !== 'granted') return;
  try {
    const notification = new Notification(title, { body, icon: '/favicon.ico', ...options });
    setTimeout(() => notification.close(), 5000);
    notification.onclick = () => { window.focus(); notification.close(); };
  } catch (error) {
    console.error('Error sending notification:', error);
  }
};

const sendNotification = (title, body, options = {}) => {
  // Check actual browser permission, not just state
  if (Notification.permission !== 'granted') return;
  
  try {
    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      vibrate: [200, 100, 200],
      ...options
    });
    setTimeout(() => notification.close(), 5000);
    notification.onclick = () => { window.focus(); notification.close(); };
  } catch (error) {
    console.error('Error sending notification:', error);
  }
};

  // Notification scheduling
  useEffect(() => {
    if (!notificationsEnabled || !isDataLoaded) return;

    // Check every minute for notifications to send
    const notificationInterval = setInterval(() => {
      const now = Date.now();

      // ONLY streak warning (12 hours before break)
      const streakWarning = getStreakWarningStatus();
      if (streakWarning?.shouldWarn && streakWarning.hoursRemaining <= 12) {
        const lastWarning = storage.getLastStreakWarning();
        const lastWarningTime = lastWarning || 0;

        // Only send warning once every 3 hours
        if (now - lastWarningTime > 3 * 60 * 60 * 1000) {
          sendNotification(
            '🚨 STREAK AT RISK!',
            `Only ${streakWarning.hoursRemaining}h ${streakWarning.minutesRemaining}m left to keep your ${streak} day streak alive!`,
            { tag: 'streak-warning' }
          );
          storage.setLastStreakWarning(now);
        }
      }

    }, 60000); // Check every minute

    return () => clearInterval(notificationInterval);
  }, [notificationsEnabled, isDataLoaded, streak]);



  // Sound system
  const playSound = (soundType) => {
    if (!soundEnabled) return;

    // Using Web Audio API for better performance
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Configure sound based on type
    switch (soundType) {
      case 'success': // Daily sent it
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.15);
        break;

      case 'recovery': // Recovery 2x
        // Double ding
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(1200, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime + 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.25);
        break;

      case 'bitchout': // Bitched out - whoosh/impact
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(50, audioContext.currentTime + 0.3);
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
        break;

      case 'weekly': // Weekly complete
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.35);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.35);
        break;

      case 'badge': // Badge unlock
        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(1500, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.25, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.4);
        break;

      case 'levelup': // Level up
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1600, audioContext.currentTime + 0.5);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.6);
        break;


      case 'click': // Generic UI click
        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.05);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.05);
        break;

      default:
        oscillator.stop(audioContext.currentTime);
        return;
    }
  };

  const COMMENT_COOLDOWN = 5000; // 5 seconds
  const MIN_COMMENT_LENGTH = 2;
  const MAX_COMMENT_LENGTH = 150;
  const MIN_SENDS_TO_POST = 3; // Must complete 3 challenges before posting
  const MAX_POSTS_PER_DAY = 5; // Max 5 posts per day
  const AUTO_HIDE_FLAG_THRESHOLD = 3; // Auto-hide at 3+ flags
  const MIN_USERNAME_LENGTH = 3;
  const MAX_USERNAME_LENGTH = 15;
  const MAX_BIO_LENGTH = 50;
  const USERNAME_CHANGE_COOLDOWN = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
  const RESERVED_USERNAMES = ['admin', 'moderator', 'mod', 'system', 'senditmaxx', 'official'];

  // Badge definitions
  const BADGE_DEFINITIONS = {
    // Consistency badges
    'first-blood': {
      name: 'FIRST BLOOD',
      description: 'You finally sent it once. Congrats.',
      icon: '🔥',
      color: 'text-red-500',
      bgColor: 'bg-red-900',
      borderColor: 'border-red-500'
    },
    'week-warrior': {
      name: 'WEEK WARRIOR',
      description: 'Completed your first weekly challenge. Big move.',
      icon: '📅',
      color: 'text-blue-400',
      bgColor: 'bg-blue-900',
      borderColor: 'border-blue-400'
    },
    'unstoppable': {
      name: 'UNSTOPPABLE',
      description: '30 days. Most people quit at level 3.',
      icon: '💀',
      color: 'text-purple-400',
      bgColor: 'bg-purple-900',
      borderColor: 'border-purple-400'
    },
    'ghost-streak': {
      name: 'GHOST STREAK',
      description: '3 challenges with no misses. Silent killer.',
      icon: '👻',
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-900',
      borderColor: 'border-cyan-400'
    },
    // Category badges
    'social-butterfly': {
      name: 'SOCIAL BUTTERFLY',
      description: '10 social challenges. You talk to humans now.',
      icon: '🧠',
      color: 'text-blue-300',
      bgColor: 'bg-blue-900',
      borderColor: 'border-blue-300'
    },
    'money-moves': {
      name: 'MONEY MOVES',
      description: '10 money challenges. Getting that bag.',
      icon: '💰',
      color: 'text-green-400',
      bgColor: 'bg-green-900',
      borderColor: 'border-green-400'
    },
    'iron-will': {
      name: 'IRON WILL',
      description: '10 gym challenges. No days off.',
      icon: '🏋️',
      color: 'text-orange-400',
      bgColor: 'bg-orange-900',
      borderColor: 'border-orange-400'
    },
    'ship-it': {
      name: 'SHIP IT',
      description: '10 creative challenges. Make stuff, ship it.',
      icon: '🎨',
      color: 'text-pink-400',
      bgColor: 'bg-pink-900',
      borderColor: 'border-pink-400'
    },
    'chaos-agent': {
      name: 'CHAOS AGENT',
      description: 'All 6 categories conquered. True chaos.',
      icon: '🌀',
      color: 'text-purple-300',
      bgColor: 'bg-purple-900',
      borderColor: 'border-purple-300'
    },
    // Behavior badges
    'execute-mode': {
      name: 'EXECUTE MODE',
      description: 'Used the countdown 10 times. Zero hesitation.',
      icon: '⚡',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-900',
      borderColor: 'border-yellow-400'
    },
    'weekly-sender': {
      name: 'WEEKLY SENDER',
      description: '3 weekly challenges crushed. Big league.',
      icon: '🏆',
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-900',
      borderColor: 'border-yellow-500'
    },
    'recovery-king': {
      name: 'RECOVERY KING',
      description: 'Came back from the dead in recovery mode.',
      icon: '🔄',
      color: 'text-teal-400',
      bgColor: 'bg-teal-900',
      borderColor: 'border-teal-400'
    },
    'community-voice': {
      name: 'COMMUNITY VOICE',
      description: '5 posts to the feed. You have something to say.',
      icon: '📢',
      color: 'text-cyan-300',
      bgColor: 'bg-cyan-900',
      borderColor: 'border-cyan-300'
    },
    'top-post': {
      name: 'TOP POST',
      description: '10 upvotes on a single post. The crowd speaks.',
      icon: '🗳️',
      color: 'text-amber-400',
      bgColor: 'bg-amber-900',
      borderColor: 'border-amber-400'
    },
    // Pact badges
    'pact-warriors': {
      name: 'PACT WARRIORS',
      description: '7-day pact streak. You and your partner don\'t quit.',
      icon: '⚔️',
      color: 'text-purple-400',
      bgColor: 'bg-purple-900',
      borderColor: 'border-purple-400'
    },
    'unbreakable': {
      name: 'UNBREAKABLE',
      description: '30-day pact streak. Absolute unit behavior.',
      icon: '🔱',
      color: 'text-violet-300',
      bgColor: 'bg-violet-900',
      borderColor: 'border-violet-300'
    },
    'carried': {
      name: 'CARRIED',
      description: 'Your pact partner broke the streak. You held it down.',
      icon: '🛡️',
      color: 'text-indigo-400',
      bgColor: 'bg-indigo-900',
      borderColor: 'border-indigo-400'
    },
    // Challenge Lab badges
    'challenge-maker': {
      name: 'CHALLENGE MAKER',
      description: 'Your submitted challenge was voted into the daily rotation.',
      icon: '⚗️',
      color: 'text-amber-400',
      bgColor: 'bg-amber-900',
      borderColor: 'border-amber-400'
    },
    'legend-maker': {
      name: 'LEGEND MAKER',
      description: '100+ people completed your community challenge. You\'re a legend.',
      icon: '⭐',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-900',
      borderColor: 'border-yellow-400'
    },
    // Premium badge
    'sent-it-pass': {
      name: 'SENT IT',
      description: 'SendIt Pass holder. One payment. Forever.',
      icon: '⚡',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-900',
      borderColor: 'border-yellow-400'
    },
    // Hard Mode badge
    'hard-sender': {
      name: 'HARD SENDER',
      description: 'Completed a Hard Mode challenge. Most people quit here.',
      icon: '💀',
      color: 'text-red-400',
      bgColor: 'bg-red-900',
      borderColor: 'border-red-500'
    },
  };




  // Comment cooldown timer
  useEffect(() => {
    if (commentCooldown > 0) {
      const timer = setTimeout(() => {
        setCommentCooldown(commentCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [commentCooldown]);

  // Load all comments
  const loadAllComments = async () => {
    try {
      const grouped = await community.fetchComments();
      setComments(grouped);
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  };

  // Check rate limiting
  const canComment = () => {
    const now = Date.now();

    // Check cooldown (5 seconds - just prevents accidental double-posts)
    if (now - lastCommentTime < COMMENT_COOLDOWN) {
      return { allowed: false, reason: `Wait ${Math.ceil((COMMENT_COOLDOWN - (now - lastCommentTime)) / 1000)}s` };
    }

    return { allowed: true };
  };

  // Get user stats for display
  const getUserStats = () => {
    if (!logs) {
      return {
        streak: 0,
        totalSends: 0,
        longestStreak: 0
      };
    }

    const totalSends = logs.filter(l => l.success && !l.autoBitched).length;

    return {
      streak,
      totalSends,
      longestStreak
    };
  };



  // Check if user can post to feed
  const canPostToFeed = () => {
    const stats = getUserStats();
    const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);

    // Must have completed at least MIN_SENDS_TO_POST challenges
    if (stats.totalSends < MIN_SENDS_TO_POST) {
      return {
        allowed: false,
        reason: `Complete ${MIN_SENDS_TO_POST - stats.totalSends} more challenge${MIN_SENDS_TO_POST - stats.totalSends !== 1 ? 's' : ''} to unlock posting`
      };
    }

    // 60-second rate limit between posts
    const lastPostTime = parseInt(localStorage.getItem('senditmaxx_last_post_time') || '0', 10);
    const elapsed = Date.now() - lastPostTime;
    if (elapsed < 60000) {
      const secondsLeft = Math.ceil((60000 - elapsed) / 1000);
      return { allowed: false, reason: `SLOW DOWN — wait ${secondsLeft} seconds` };
    }

    // Check daily post limit
    const postsToday = communityFeed.filter(p =>
      p.userId === userId &&
      p.timestamp >= twentyFourHoursAgo
    ).length;

    if (postsToday >= MAX_POSTS_PER_DAY) {
      return {
        allowed: false,
        reason: `Daily limit reached (${MAX_POSTS_PER_DAY} posts/day)`
      };
    }

    return { allowed: true };
  };

  // Add comment
  const addComment = async (postId) => {
    const trimmedText = commentText.trim();

    if (trimmedText.length < MIN_COMMENT_LENGTH) {
      alert(`Comment must be at least ${MIN_COMMENT_LENGTH} characters`);
      return;
    }
    if (trimmedText.length > MAX_COMMENT_LENGTH) {
      alert(`Comment must be under ${MAX_COMMENT_LENGTH} characters`);
      return;
    }
    if (trimmedText === lastCommentText) {
      alert('Cannot post duplicate comment');
      return;
    }

    const rateCheck = canComment();
    if (!rateCheck.allowed) { alert(rateCheck.reason); return; }

    setIsPostingComment(true);
    try {
      const stats = getUserStats();
      const newComment = {
        id: 'comment_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        postId,
        userId,
        username,
        streak: stats.streak,
        text: trimmedText,
        timestamp: Date.now(),
        flags: 0,
        flaggedBy: []
      };

      await community.insertComment(newComment);

      setComments(prev => {
        const updated = { ...prev };
        if (!updated[postId]) updated[postId] = [];
        updated[postId] = [...updated[postId], newComment];
        return updated;
      });

      setLastCommentTime(Date.now());
      setLastCommentText(trimmedText);
      setCommentCooldown(COMMENT_COOLDOWN / 1000);
      setCommentText('');
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Failed to post comment');
    } finally {
      setIsPostingComment(false);
    }
  };

  // Delete comment (own comments or if you're the post author)
  const deleteComment = async (commentId, postId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      const comment = comments[postId]?.find(c => c.id === commentId);
      const post = communityFeed.find(p => p.id === postId);
      if (!comment) return;
      if (comment.userId !== userId && post?.userId !== userId) {
        alert('You can only delete your own comments or comments on your posts');
        return;
      }
      await community.removeComment(commentId);
      setComments(prev => {
        const updated = { ...prev };
        if (updated[postId]) {
          updated[postId] = updated[postId].filter(c => c.id !== commentId);
        }
        return updated;
      });
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  // Flag comment
  const flagComment = async (commentId, postId) => {
    try {
      const comment = comments[postId]?.find(c => c.id === commentId);
      if (!comment) return;
      if (comment.userId === userId) { alert('Cannot flag your own comment'); return; }
      if (comment.flaggedBy?.includes(userId)) { alert('You already flagged this comment'); return; }

      await community.flagCommentInDb(commentId, userId);

      setComments(prev => {
        const updated = { ...prev };
        if (updated[postId]) {
          updated[postId] = updated[postId].map(c =>
            c.id === commentId
              ? { ...c, flags: c.flags + 1, flaggedBy: [...c.flaggedBy, userId] }
              : c
          );
        }
        return updated;
      });
    } catch (err) {
      console.error('Error flagging comment:', err);
    }
  };
  useEffect(() => {
    if (undoTimer && undoCountdown > 0) {
      const timer = setTimeout(() => {
        setUndoCountdown(undoCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (undoTimer && undoCountdown === 0) {
      // Time's up, commit the action
      setUndoTimer(null);
      setUndoAction(null);
      setUndoCountdown(5);
    }
  }, [undoTimer, undoCountdown]);

  const startUndoTimer = (action) => {
    setUndoAction(action);
    setUndoCountdown(5);
    setUndoTimer(Date.now());
  };

  const executeUndo = () => {
    if (!undoAction) return;

    // Restore previous state
    if (undoAction.type === 'daily') {
      storage.setLogs(undoAction.previousLogs);

      // Restore all stats
      storage.setStreak(undoAction.previousStreak);
      storage.setScore(undoAction.previousScore);
      storage.setRecoveryMode(undoAction.previousRecoveryMode);

      const restoredChallenge = { ...dailyChallenge, completed: false };
      storage.setDailyChallenge({
        challenge: restoredChallenge,
        date: new Date()
      });
    } else if (undoAction.type === 'weekly') {
      storage.setLogs(undoAction.previousLogs);

      // Restore all stats
      storage.setStreak(undoAction.previousStreak);
      storage.setScore(undoAction.previousScore);

      storage.setWeeklyCompleted(false);
    }

    // Clear undo
    setUndoTimer(null);
    setUndoAction(null);
    setUndoCountdown(5);
  };

  const cancelUndo = () => {
    setUndoTimer(null);
    setUndoAction(null);
    setUndoCountdown(5);
  };
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Initialize user ID and load data
  useEffect(() => {
    const initializeUser = async () => {
      let session;
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        session = data.session;
      } catch (error) {
        console.error('Unable to check the current Supabase session:', error);
        setAuthGateMode('choose');
        return;
      }

      if (!session) {
        setAuthGateMode('choose');
        return;
      }

      const recoveryRequested = new URLSearchParams(window.location.search).get('auth') === 'recovery';
      setAuthGateMode(recoveryRequested ? 'update_password' : null);
      const hasSession = Boolean(session.user.id);
      const uid = session.user.id;
      setHasCloudSession(hasSession);
      setIsAnonymousUser(Boolean(session.user.is_anonymous));
      setAuthEmail(session.user.email || '');
      setAuthEmailConfirmed(Boolean(session.user.email_confirmed_at));
      setUserId(uid);

      // Check if user has set a username
      const savedUsername = storage.getUsername();
      const savedBio = storage.getBio();
      const savedLastChange = storage.getLastUsernameChange();

      if (savedUsername) {
        setUsername(savedUsername);
        if (savedBio) setBio(savedBio);
        if (savedLastChange) setLastUsernameChange(savedLastChange);
      } else {
        // Show username setup on first load
        setShowUsernameSetup(true);
      }

      // Load user votes
      community.fetchUserVotes(uid).then(votes => {
        setUserVotes(votes);
      }).catch(() => {
        // Fall back to localStorage votes if offline
        setUserVotes(storage.getUserVotes());
      });


      let savedLogs = [];
      let savedDecisions = [];
      let savedPendingOutcomes = [];
      let savedDailyChallenge = null;
      let savedWeeklyChallenge = null;
      let savedWeeklyCompleted = false;
      let savedWeeklyStartDate = null;
      let savedLastCompletionDate = null;

      let savedStreak = 0;

      try {
        // Import legacy browser data before loading cloud state. If the import
        // fails, use the local fallback below and retry on the next launch.
        if (session.user.is_anonymous) {
          await migrateIfNeeded(uid, {
            force: localStorage.getItem('senditmaxx_force_migration') === 'true',
          });
        }

        const [stats, dbLogs, dbDecisions, challengeState] = await Promise.all([
          loadUserStats(uid),
          loadLogs(uid),
          loadDecisions(uid),
          loadChallengeState(uid),
        ]);

        // Stats
        savedStreak = stats.streak || 0;
        setStreak(savedStreak);
        setSendItScore(stats.score || 0);
        setLongestStreak(stats.longest_streak || 0);
        setRecoveryMode(stats.recovery_mode || false);
        setBadges(stats.badges || []);
        setCountdownUses(stats.countdown_uses || 0);
        setSoundEnabled(stats.sound_enabled ?? true);
        if (stats.username !== null && stats.username !== undefined) {
          setUsername(stats.username || '');
          storage.setUsername(stats.username || '');
          if (stats.username) setShowUsernameSetup(false);
        }
        if (stats.bio !== null && stats.bio !== undefined) {
          setBio(stats.bio || '');
          storage.setBio(stats.bio || '');
        }
        if (stats.last_username_change !== undefined) {
          setLastUsernameChange(stats.last_username_change || null);
          if (stats.last_username_change) {
            storage.setLastUsernameChange(stats.last_username_change);
          } else {
            storage.removeStorageItem('last_username_change');
          }
        }
        storage.setOnboardingComplete(stats.onboarding_complete ?? storage.getOnboardingComplete());
        if (stats.visit_count !== null && stats.visit_count !== undefined) {
          storage.setVisitCount(stats.visit_count);
        }

        savedLastCompletionDate = stats.last_completion !== undefined
          ? stats.last_completion
          : storage.getLastCompletion();
        setLastCompletionAt(savedLastCompletionDate);
        if (stats.last_completion) {
          storage.setLastCompletion(stats.last_completion);
        } else if (stats.last_completion !== undefined) {
          storage.removeStorageItem('last_completion');
        }

        // Logs + decisions
        savedLogs = dbLogs;
        savedDecisions = dbDecisions;
        setLogs(dbLogs);
        setSavedDecisions(dbDecisions);

        // Pending outcomes are stored on their corresponding cloud log rows.
        savedPendingOutcomes = dbLogs.filter(log => log.pendingOutcome).map(log => log.id);
        setPendingOutcomes(savedPendingOutcomes);
        storage.setPendingOutcomes(savedPendingOutcomes);

        // Challenge state
        if (challengeState) {
          savedDailyChallenge = challengeState.daily_challenge
            ? { challenge: challengeState.daily_challenge, date: challengeState.daily_date }
            : null;
          if (savedDailyChallenge) storage.setDailyChallenge(savedDailyChallenge);
          savedWeeklyChallenge = challengeState.weekly_challenge || null;
          savedWeeklyCompleted = challengeState.weekly_completed || false;
          savedWeeklyStartDate = challengeState.weekly_start_date || null;
        }
        setWeeklyCompleted(savedWeeklyCompleted);
        setWeeklyStartDate(savedWeeklyStartDate);

      } catch (err) {
        // ⚠️ Supabase failed — fall back to localStorage so app still works
        console.warn('Supabase load failed, using localStorage fallback', err);
        savedStreak = storage.getStreak();
        setStreak(savedStreak);
        setSendItScore(storage.getScore());
        savedLogs = storage.getLogs();
        savedDecisions = storage.getDecisions();
        savedPendingOutcomes = storage.getPendingOutcomes();
        savedDailyChallenge = storage.getDailyChallenge();
        savedWeeklyChallenge = storage.getWeeklyChallenge();
        savedWeeklyCompleted = storage.getWeeklyCompleted();
        savedWeeklyStartDate = storage.getWeeklyStartDate();
        savedLastCompletionDate = storage.getLastCompletion();
        setLastCompletionAt(savedLastCompletionDate);
        setLongestStreak(storage.getLongestStreak());
        setRecoveryMode(storage.getRecoveryMode());
        setBadges(storage.getBadges());
        setCountdownUses(storage.getCountdownUses());
        setSoundEnabled(storage.getSoundEnabled());
        setLogs(savedLogs);
        setSavedDecisions(savedDecisions);
        setPendingOutcomes(savedPendingOutcomes);
        setWeeklyCompleted(savedWeeklyCompleted);
        setWeeklyStartDate(savedWeeklyStartDate);
      }

      // Check if streak should break (no completion in 48+ hours)
      if (savedLastCompletionDate && savedStreak > 0) {
        const lastCompletion = savedLastCompletionDate;
        const now = Date.now();
        const twoDaysMs = 48 * 60 * 60 * 1000;

        if ((now - lastCompletion) >= twoDaysMs) {
          setStreak(0);
          setRecoveryMode(true);
          storage.setStreak(0);
          storage.setRecoveryMode(true);
        }
      }

      // Helper: check for a pending custom challenge; fall back to normal generation
      const promotedChallengesRef = [];
      const applyNextDailyChallenge = async (userId) => {
        // 1. Try Supabase custom challenge
        let pending = null;
        try {
          pending = await loadAndClearCustomChallenge(userId);
        } catch (error) {
          console.error('Unable to load pending custom challenge from Supabase:', error);
        }

        // 2. localStorage fallback if Supabase missed
        if (!pending) {
          try {
            const raw = localStorage.getItem('senditmaxx_custom_challenge');
            if (raw) { pending = JSON.parse(raw); }
          } catch { /* silent */ }
        }

        if (pending) {
          const customChallenge = {
            text: pending.text,
            category: pending.category,
            points: pending.points || 5,
            completed: false,
            isCustom: true,
          };
          setDailyChallenge(customChallenge);
          storage.setDailyChallenge({ challenge: customChallenge, date: new Date() });
          localStorage.removeItem('senditmaxx_custom_challenge');
        } else {
          generateNewDailyChallenge();
        }
      };

      // Load or generate daily challenge
      if (savedDailyChallenge) {
        const saved = savedDailyChallenge;
        const savedDate = new Date(saved.date).toDateString();
        const today = new Date().toDateString();

        if (savedDate === today) {
          setDailyChallenge(saved.challenge);
          loadChallengeCompletionCount('daily', saved.challenge.text);

          // Check if 24 hours have passed and challenge wasn't completed
          const challengeTimestamp = new Date(saved.date).getTime();
          const now = Date.now();
          const twentyFourHours = 24 * 60 * 60 * 1000;

          if (!saved.challenge.completed && (now - challengeTimestamp) >= twentyFourHours) {
            // Auto-bitch out
            const autoBitchLog = {
              id: Date.now(),
              challenge: saved.challenge.text,
              points: saved.challenge.points,
              earnedPoints: 0,
              recoveryBonus: false,
              category: saved.challenge.category,
              success: false,
              outcome: null,
              timestamp: now,
              date: new Date().toLocaleDateString(),
              autoBitched: true
            };

            const updatedLogs = [autoBitchLog, ...logs];
            setLogs(updatedLogs);
            storage.setLogs(updatedLogs);

            // Mark as completed so it doesn't auto-bitch again
            const completedChallenge = { ...saved.challenge, completed: true };
            setDailyChallenge(completedChallenge);
            storage.setDailyChallenge({
              challenge: completedChallenge,
              date: saved.date
            });
          }
        } else {
          await applyNextDailyChallenge(uid, promotedChallengesRef);
        }
      } else {
        await applyNextDailyChallenge(uid, promotedChallengesRef);
      }

      // Load or generate weekly challenge
      if (savedWeeklyChallenge) {
        const saved = savedWeeklyChallenge;
        setWeeklyChallenge(saved);
        loadChallengeCompletionCount('weekly', saved.text);

        // Check weekly challenge status
        if (savedWeeklyStartDate) {
          const weekStart = parseInt(savedWeeklyStartDate);
          const now = Date.now();
          const oneWeek = 7 * 24 * 60 * 60 * 1000;
          const timeElapsed = now - weekStart;

          if (timeElapsed >= oneWeek) {
            // Week has passed - check if it needs auto-bitch or new challenge
            if (!savedWeeklyCompleted) {
              // Auto-bitch out on weekly
              const autoBitchLog = {
                id: Date.now(),
                challenge: saved.text,
                points: saved.points,
                earnedPoints: 0,
                recoveryBonus: false,
                category: 'weekly',
                success: false,
                outcome: null,
                timestamp: now,
                date: new Date().toLocaleDateString(),
                autoBitched: true,
                isWeekly: true
              };

              const updatedLogs = [autoBitchLog, ...logs];
              setLogs(updatedLogs);
              storage.setLogs(updatedLogs);
            }

            // Generate new weekly challenge (week is over)
            generateNewWeeklyChallenge();
          } else {
            // Week still in progress - calculate days remaining
            const daysLeft = Math.ceil((oneWeek - timeElapsed) / (24 * 60 * 60 * 1000));
            setDaysUntilNextWeekly(daysLeft);
          }
        }
      } else {
        generateNewWeeklyChallenge();
      }

      const hasSeenOnboarding = storage.getOnboardingComplete();
      if (!hasSeenOnboarding) {
        const samplePosts = [
          { id: 'sample1', userId: 'system', username: 'DeadliftDan', bio: 'Lift heavy think less', text: "Asked my boss for a 20% raise. Got 15%. Would've gotten 0% if I kept overthinking it.", category: 'money', vibe: 'lockedin', sendAgain: true, votes: 127, timestamp: Date.now() - 86400000, flags: 0, flaggedBy: [], userStats: { streak: 12, totalSends: 34, longestStreak: 12 } },
          { id: 'sample2', userId: 'system2', username: 'ChaosKing', bio: '', text: "Told my crush I liked them. Got rejected. But honestly feel 10x lighter now.", category: 'social', vibe: 'rejected', sendAgain: true, votes: 89, timestamp: Date.now() - 172800000, flags: 0, flaggedBy: [], userStats: { streak: 5, totalSends: 18, longestStreak: 8 } },
          { id: 'sample3', userId: 'system3', username: 'SeedOilSkipper', bio: 'No seed oils ever', text: "Deleted my meal prep spreadsheet. Just eating when I'm hungry now. Lost 5lbs somehow.", category: 'anti-opt', vibe: 'sentanyway', sendAgain: true, votes: 156, timestamp: Date.now() - 259200000, flags: 0, flaggedBy: [], userStats: { streak: 21, totalSends: 67, longestStreak: 21 } },
          { id: 'sample4', userId: 'system4', username: 'GymRat_Sarah', bio: '300lb deadlift incoming', text: "Asked the biggest dude at the gym for form tips. Now we're lifting partners.", category: 'gym', vibe: 'bigW', sendAgain: true, votes: 201, timestamp: Date.now() - 345600000, flags: 0, flaggedBy: [], userStats: { streak: 15, totalSends: 42, longestStreak: 19 } },
          { id: 'sample5', userId: 'system5', username: 'CreativeCarl', bio: 'Ship first fix later', text: "Posted my unfinished novel online. Someone DMed me asking to buy the rights. Still processing this.", category: 'creative', vibe: 'unhinged', sendAgain: false, votes: 178, timestamp: Date.now() - 432000000, flags: 0, flaggedBy: [], userStats: { streak: 3, totalSends: 23, longestStreak: 9 } },
          { id: 'sample6', userId: 'system6', username: 'AnxietyAnnihilator', bio: '', text: "Quit my job with no backup plan. 2 weeks later landed something paying 30% more.", category: 'chaos', vibe: 'plot_twist', sendAgain: true, votes: 189, timestamp: Date.now() - 518400000, flags: 0, flaggedBy: [], userStats: { streak: 6, totalSends: 14, longestStreak: 7 } },
        ];
        samplePosts.forEach(p => storage.savePost(p.id, p));
        setShowOnboarding(true);
      }

      loadCommunityFeed();
      loadAllComments();

      // Load badges
      const savedBadges = storage.getBadges();
      setBadges(savedBadges);

      // Challenge Lab: load promoted community challenges for daily rotation
      labService.fetchPromotedChallenges().then(picks => {
        setPromotedChallenges(picks);
      }).catch(() => {});

      // Challenge Lab: check if user earns challenge-maker / legend-maker badges
      labService.checkSubmitterBadges(uid).then(({ challengeMaker, legendMaker }) => {
        if (challengeMaker && !savedBadges.includes('challenge-maker')) unlockBadge('challenge-maker');
        if (legendMaker && !savedBadges.includes('legend-maker'))     unlockBadge('legend-maker');
      }).catch(() => {});

      // Premium status is granted by the trusted payment webhook only.
      labService.fetchUserPremiumStatus(uid).then(p => setIsPremium(!!p)).catch(() => {});

      // Load countdown uses
      setCountdownUses(storage.getCountdownUses());

      // PWA install prompt
      window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
      });

      // Track visits
      const visitCount = storage.getVisitCount();
      storage.setVisitCount(visitCount + 1);

      // Check for pact invite code in URL (?pact=XXXXXXXX)
      const pactCodeFromUrl = new URLSearchParams(window.location.search).get('pact');
      if (pactCodeFromUrl) {
        setPendingPactCode(pactCodeFromUrl.trim().toUpperCase());
        setCurrentView('pact');
        window.history.replaceState({}, '', '/');
      }

      // Mark data as loaded
      setIsDataLoaded(true);

      // Load sound preference
      const savedSound = storage.getSoundEnabled();
      setSoundEnabled(savedSound);

      // Check notification permission status
      if ('Notification' in window) {
        setNotificationPermission(Notification.permission);
        setNotificationsEnabled(Notification.permission === 'granted');
      }
    };

    initializeUser();
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setAuthGateMode('update_password');
      }
      if (session?.user) {
        setIsAnonymousUser(Boolean(session.user.is_anonymous));
        setAuthEmail(session.user.email || '');
        setAuthEmailConfirmed(Boolean(session.user.email_confirmed_at));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Keep cloud-backed progress current after the one-time legacy import.
  // localStorage remains the offline cache; cloud errors are logged so they
  // can be diagnosed and a later state change or reload can retry the save.
  useEffect(() => {
    if (!isDataLoaded || !hasCloudSession || !userId) return undefined;

    updateUserStats(userId, {
      username,
      bio,
      score: sendItScore,
      streak,
      longest_streak: longestStreak,
      badges,
      countdown_uses: countdownUses,
      weekly_start_date: weeklyStartDate,
      onboarding_complete: storage.getOnboardingComplete(),
      visit_count: storage.getVisitCount(),
      recovery_mode: recoveryMode,
      last_completion: lastCompletionAt,
      last_username_change: lastUsernameChange,
      sound_enabled: soundEnabled,
    }).catch(error => console.error('Failed to save user progress to Supabase:', error));
    return undefined;
  }, [
    isDataLoaded, hasCloudSession, userId, username, bio, sendItScore,
    streak, longestStreak, badges, countdownUses, weeklyStartDate,
    recoveryMode, lastCompletionAt, lastUsernameChange, soundEnabled,
  ]);

  useEffect(() => {
    if (!isDataLoaded || !hasCloudSession || !userId) return undefined;

    saveLogs(userId, logs, pendingOutcomes)
      .catch(error => console.error('Failed to save challenge logs to Supabase:', error));
    return undefined;
  }, [isDataLoaded, hasCloudSession, userId, logs, pendingOutcomes]);

  useEffect(() => {
    if (!isDataLoaded || !hasCloudSession || !userId) return undefined;

    saveDecisions(userId, savedDecisions)
      .catch(error => console.error('Failed to save decisions to Supabase:', error));
    return undefined;
  }, [isDataLoaded, hasCloudSession, userId, savedDecisions]);

  useEffect(() => {
    if (!isDataLoaded || !hasCloudSession || !userId) return undefined;

    const dailyState = storage.getDailyChallenge();
    saveChallengeState(userId, {
      daily_challenge: dailyChallenge || null,
      daily_date: dailyState?.date || null,
      weekly_challenge: weeklyChallenge || null,
      weekly_completed: weeklyCompleted,
      weekly_start_date: weeklyStartDate,
    }).catch(error => console.error('Failed to save challenge state to Supabase:', error));
    return undefined;
  }, [
    isDataLoaded, hasCloudSession, userId, dailyChallenge, weeklyChallenge,
    weeklyCompleted, weeklyStartDate,
  ]);

  // Validate username
  const validateUsername = (name) => {
    const trimmed = name.trim();

    if (trimmed.length < MIN_USERNAME_LENGTH) {
      return `Username must be at least ${MIN_USERNAME_LENGTH} characters`;
    }

    if (trimmed.length > MAX_USERNAME_LENGTH) {
      return `Username must be under ${MAX_USERNAME_LENGTH} characters`;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
      return 'Username can only contain letters, numbers, and underscores';
    }

    if (RESERVED_USERNAMES.includes(trimmed.toLowerCase())) {
      return 'This username is reserved';
    }

    // Check if username is taken
    const allPosts = storage.getAllPosts();
    for (const post of allPosts) {
      if (post.username && post.username.toLowerCase() === trimmed.toLowerCase() && post.userId !== userId) {
        return 'Username already taken';
      }
    }

    return null;
  };



  // Save username
  const saveUsername = async () => {
    const error = validateUsername(usernameInput);
    if (error) {
      setUsernameError(error);
      return;
    }

    const trimmed = usernameInput.trim();
    const trimmedBio = bioInput.trim();

    // Server-side duplicate check — skip if username hasn't changed
    if (trimmed.toLowerCase() !== username.toLowerCase() && userId) {
      try {
        const { data: existing } = await supabase
          .from('user_stats')
          .select('user_id')
          .ilike('username', trimmed)
          .neq('user_id', userId)
          .limit(1);
        if (existing && existing.length > 0) {
          setUsernameError('USERNAME TAKEN — pick another');
          return;
        }
      } catch (err) {
        console.error('Username availability check failed:', err);
      }
    }

    setUsername(trimmed);
    setBio(trimmedBio);
    storage.setUsername(trimmed);
    storage.setBio(trimmedBio);

    // Only update last change timestamp if username actually changed
    if (trimmed !== username) {
      const now = Date.now();
      setLastUsernameChange(now);
      storage.setLastUsernameChange(now);
    }

    // Save email to Supabase if provided and valid
    setShowUsernameSetup(false);
    setShowEditProfile(false);
    setUsernameInput('');
    setBioInput('');
    setUsernameError('');
  };

  // Check if username can be changed
  const canChangeUsername = () => {
    if (!lastUsernameChange) return { allowed: true };

    const now = Date.now();
    const timeSinceChange = now - lastUsernameChange;

    if (timeSinceChange < USERNAME_CHANGE_COOLDOWN) {
      const daysRemaining = Math.ceil((USERNAME_CHANGE_COOLDOWN - timeSinceChange) / (24 * 60 * 60 * 1000));
      return {
        allowed: false,
        daysRemaining
      };
    }

    return { allowed: true };
  };

  // Badge unlock function
  const unlockBadge = (badgeId) => {
    if (badges.includes(badgeId)) return; // Already unlocked

    const newBadges = [...badges, badgeId];
    setBadges(newBadges);
    storage.setBadges(newBadges);

    // Show unlock animation
    setNewlyUnlockedBadge(BADGE_DEFINITIONS[badgeId]);
    setShowBadgeUnlock(true);
    setShowConfetti(true);
    playSound('badge');

    setTimeout(() => {
      setShowBadgeUnlock(false);
      setShowConfetti(false);
      setNewlyUnlockedBadge(null);
    }, 3000);
  };



  // Unlock premium badge when isPremium becomes true
  useEffect(() => {
    if (isPremium && isDataLoaded && !badges.includes('sent-it-pass')) {
      unlockBadge('sent-it-pass');
    }
  }, [isPremium, isDataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check badge conditions
  useEffect(() => {
    if (!logs || logs.length === 0 || !isDataLoaded) return;

    const successLogs = logs.filter(l => l.success);

    // --- Consistency ---
    if (successLogs.length >= 1 && !badges.includes('first-blood')) {
      unlockBadge('first-blood');
    }
    if (streak >= 3 && !badges.includes('ghost-streak')) {
      unlockBadge('ghost-streak');
    }
    if (streak >= 7 && !badges.includes('week-warrior')) {
      unlockBadge('week-warrior');
    }
    if (streak >= 30 && !badges.includes('unstoppable')) {
      unlockBadge('unstoppable');
    }

    // --- Category (10 successful completions each) ---
    const categoryBadges = {
      'social': 'social-butterfly',
      'money': 'money-moves',
      'gym': 'iron-will',
      'creative': 'ship-it',
    };
    Object.entries(categoryBadges).forEach(([cat, badgeId]) => {
      if (successLogs.filter(l => l.category === cat).length >= 10 && !badges.includes(badgeId)) {
        unlockBadge(badgeId);
      }
    });

    // Chaos Agent - at least 1 successful completion in all 6 categories
    const allCategories = ['social', 'money', 'anti-opt', 'gym', 'creative', 'chaos'];
    const completedCategories = new Set(successLogs.map(l => l.category));
    if (allCategories.every(cat => completedCategories.has(cat)) && !badges.includes('chaos-agent')) {
      unlockBadge('chaos-agent');
    }

    // --- Behavior ---
    if (logs.filter(l => l.isWeekly && l.success).length >= 3 && !badges.includes('weekly-sender')) {
      unlockBadge('weekly-sender');
    }
    if (successLogs.some(l => l.recoveryBonus) && !badges.includes('recovery-king')) {
      unlockBadge('recovery-king');
    }
    if (countdownUses >= 10 && !badges.includes('execute-mode')) {
      unlockBadge('execute-mode');
    }

    // Community Voice - 5 posts by this user
    if (userId) {
      const userPosts = communityFeed.filter(p => p.userId === userId);
      if (userPosts.length >= 5 && !badges.includes('community-voice')) {
        unlockBadge('community-voice');
      }
      if (userPosts.some(p => p.votes >= 10) && !badges.includes('top-post')) {
        unlockBadge('top-post');
      }
    }
  }, [logs, streak, badges, isDataLoaded, countdownUses, communityFeed, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const getUserCategoryBreakdown = (userIdToCheck) => {
    // Get all posts from this user
    const userPosts = communityFeed.filter(p => p.userId === userIdToCheck);

    // Count by category
    const categoryCounts = {};
    userPosts.forEach(post => {
      categoryCounts[post.category] = (categoryCounts[post.category] || 0) + 1;
    });

    // Convert to array and sort
    return Object.entries(categoryCounts)
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count);
  };

  // Get recent sends for current user
  // Get recent sends for current user
  const getRecentSends = () => {
    try {
      if (!logs || !Array.isArray(logs) || logs.length === 0) return [];

      return logs
        .filter(l => l && l.success && !l.autoBitched)
        .slice(0, 3)
        .map(l => ({
          challenge: l.challenge,
          category: l.category,
          timestamp: l.timestamp,
          isWeekly: l.isWeekly,
          isCustom: l.isCustom
        }));
    } catch (err) {
      console.error('getRecentSends error:', err);
      return [];
    }
  };

  // Get recent sends for a specific user
  const getUserRecentSends = (userIdToCheck) => {
    if (!communityFeed) return [];

    // Get posts from this user that indicate successful sends
    const userPosts = communityFeed
      .filter(p => p.userId === userIdToCheck && p.sendAgain === true)
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 5);

    return userPosts.map(post => ({
      challenge: post.text,
      category: post.category,
      timestamp: post.timestamp,
      isWeekly: false,
      isCustom: false
    }));
  };

  // Load community feed from storage
  const loadCommunityFeed = async () => {
    setIsLoadingFeed(true);
    try {
      const rows = await community.fetchPosts();
      setCommunityFeed(rows.map(community.mapPost));
    } catch (err) {
      console.error('Error loading feed:', err);
      setCommunityFeed([]);
    } finally {
      setIsLoadingFeed(false);
    }
  };

  // Save user votes to localStorage
  const saveUserVotes = (votes) => {
    storage.setUserVotes(votes);
  };

  // Flag post
  const flagPost = async (postId) => {
    try {
      const post = communityFeed.find(p => p.id === postId);
      if (!post) return;
      if (post.userId === userId) { alert('Cannot flag your own post'); return; }
      if (post.flaggedBy?.includes(userId)) { alert('You already flagged this post'); return; }

      await community.flagPostInDb(postId, userId);
      setCommunityFeed(feed =>
        feed.map(p => p.id === postId
          ? { ...p, flags: p.flags + 1, flaggedBy: [...(p.flaggedBy || []), userId] }
          : p
        )
      );
    } catch (err) {
      console.error('Error flagging post:', err);
    }
  };

  // Delete post (own posts only)
  const deletePost = async (postId) => {
      if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      const post = communityFeed.find(p => p.id === postId);
      if (!post || post.userId !== userId) {
        alert('You can only delete your own posts');
        return;
      }
      await community.removePost(postId);
      setCommunityFeed(feed => feed.filter(p => p.id !== postId));
      const updatedComments = { ...comments };
      delete updatedComments[postId];
      setComments(updatedComments);
    } catch (err) {
      console.error('Error deleting post:', err);
    }
  };

  const upvotePost = async (postId) => {
    if (!userId) return;

    try {
      if (userVotes[postId]) {
        // Remove vote
        await community.removeVote(postId, userId);
        setCommunityFeed(feed =>
          feed.map(p => p.id === postId ? { ...p, votes: Math.max(0, p.votes - 1) } : p)
        );
        const newVotes = { ...userVotes };
        delete newVotes[postId];
        setUserVotes(newVotes);
        storage.setUserVotes(newVotes);
      } else {
        // Add vote
        await community.addVote(postId, userId);
        setCommunityFeed(feed =>
          feed.map(p => p.id === postId ? { ...p, votes: p.votes + 1 } : p)
        );
        const newVotes = { ...userVotes, [postId]: true };
        setUserVotes(newVotes);
        storage.setUserVotes(newVotes);
      }
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  // Post to community feed
  const addToFeed = async () => {
    if (!shareText.trim() || shareText.length > MAX_POST_LENGTH) {
      setPostError(`Post must be between 1-${MAX_POST_LENGTH} characters`);
      return;
    }

    const postCheck = canPostToFeed();
    if (!postCheck.allowed) {
      setPostError(postCheck.reason);
      return;
    }

    setIsPosting(true);
    setPostError(null);

    try {
      const stats = getUserStats();
      const newPost = {
        id: 'post_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        userId,
        username,
        bio,
        userStats: {
          streak: stats.streak,
          totalSends: stats.totalSends,
          longestStreak: stats.longestStreak
        },
        category: shareCategory,
        vibe: shareVibe,
        sendAgain: wouldSendAgain,
        text: shareText.trim(),
        votes: 0,
        timestamp: Date.now(),
        flags: 0,
        flaggedBy: []
      };

      await community.insertPost(newPost);
      localStorage.setItem('senditmaxx_last_post_time', Date.now().toString());

      // Optimistic update
      setCommunityFeed(prev => [newPost, ...prev]);
      setShareText('');
      setShareCategory('social');
      setShareVibe('lockedin');
      setWouldSendAgain(true);
      setCommunitySubView('feed');

      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } catch (err) {
      console.error('Error posting:', err);
      setPostError('Failed to post. Please try again.');
    } finally {
      setIsPosting(false);
    }
  };



  // Filter and sort feed
  const getFilteredFeed = () => {
    let filtered = [...communityFeed];

    // Auto-hide flagged content
    filtered = filtered.filter(post => (post.flags || 0) < AUTO_HIDE_FLAG_THRESHOLD);

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    // Filter by "would send again"
    if (showSendAgainOnly) {
      filtered = filtered.filter(post => post.sendAgain === true);
    }

    // Sort by filter type
    if (feedFilter === 'top') {
      // Hot algorithm - balances votes with recency
      const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      filtered = filtered
        .filter(post => post.timestamp >= oneWeekAgo)
        .sort((a, b) => hotScore(b) - hotScore(a));
    } else if (feedFilter === 'recent') {
      filtered = filtered.sort((a, b) => b.timestamp - a.timestamp);
    }

    return filtered;
  };

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      forceDecision();
    }
  }, [countdown]);

  const startDecisionCountdown = () => {
    if (coinFlipMode) {
      if (!optionA.trim() || !optionB.trim()) return;

      const newDecision = {
        id: Date.now(),
        text: `${optionA} VS ${optionB}`,
        savedAt: new Date().toLocaleDateString()
      };
      const updatedDecisions = [newDecision, ...savedDecisions];
      setSavedDecisions(updatedDecisions);
      storage.setDecisions(updatedDecisions);
      setCurrentDecision(newDecision);

      setCountdown(3);
    } else {
      // Decision mode
      if (!decisionText.trim()) {
        setInputError("Type your decision first — stop overthinking it!");
        return;
      }
      setInputError('');

      // Coin flip mode  
      if (!optionA.trim() || !optionB.trim()) {
        setInputError("Fill in both options first.");
        return;
      }
      setInputError('');

      const newDecision = {
        id: Date.now(),
        text: decisionText,
        savedAt: new Date().toLocaleDateString()
      };
      const updatedDecisions = [newDecision, ...savedDecisions];
      setSavedDecisions(updatedDecisions);
      storage.setDecisions(updatedDecisions);
      setCurrentDecision(newDecision);

      setCountdown(10);
    }

    setShowResult(false);
    setCoinResult(null);

    const newCount = countdownUses + 1;
    setCountdownUses(newCount);
    storage.setCountdownUses(newCount);
  };

  const forceDecision = () => {
    if (coinFlipMode) {
      const flip = Math.random() > 0.5;
      const winner = flip ? optionA : optionB;
      const side = flip ? "HEADS" : "TAILS";
      setCoinResult({ winner, side });
      setShowResult(`${side} - ${winner.toUpperCase()}`);
    } else {
      const decision = Math.random() > 0.5 ? "SEND IT ✓" : "SEND IT ANYWAY ✓";
      setShowResult(decision);
    }
    setCountdown(null);
  };

  // Check for level up and trigger effects
  const checkLevelUp = (newScore) => {
    const newLevel = getLevelInfo(newScore).level;
    const oldLevel = getLevelInfo(sendItScore).level;

    if (newLevel > oldLevel) {
      setLevelUpData({
        oldLevel,
        newLevel,
        points: newScore
      });
      setShowLevelUp(true);
      playSound('levelup');
      setCelebrationType('levelup');
      setShowConfetti(true);

      setTimeout(() => {
        setShowLevelUp(false);
        setShowConfetti(false);
        setCelebrationType(null);
      }, 3000);
    }
  };

  const completeWeeklyChallenge = (success) => {
    if (success) {
      console.log('Weekly completed - triggering celebration');
      playSound('weekly');
      setCelebrationType('weekly');
      setShowConfetti(true);
      setPulseScore(true);
      setTimeout(() => {
        setShowConfetti(false);
        setCelebrationType(null);
      }, 3000);
      setTimeout(() => {
        setPulseScore(false);
      }, 2400);
    } else {
      console.log('Weekly bitched - triggering shake');
      playSound('bitchout');
      setShakeWeekly(true);
      setTimeout(() => setShakeWeekly(false), 600);

      // Start undo timer for "bitched out" actions - store ALL previous state
      startUndoTimer({
        type: 'weekly',
        previousLogs: logs,
        previousStreak: streak,
        previousScore: sendItScore
      });
    }

    const newLog = {
      id: Date.now(),
      challenge: weeklyChallenge.text,
      points: weeklyChallenge.points,
      earnedPoints: success ? weeklyChallenge.points : 0,
      recoveryBonus: false,
      category: 'weekly',
      success,
      outcome: null,
      timestamp: Date.now(),
      date: new Date().toLocaleDateString(),
      isWeekly: true
    };

    const newLogs = [newLog, ...logs];
    setLogs(newLogs);
    storage.setLogs(newLogs);

    // Mark as completed (but don't generate new challenge yet)
    setWeeklyCompleted(true);
    storage.setWeeklyCompleted(true);

    if (success) {
      const newStreak = streak + 1;
      const newScore = sendItScore + weeklyChallenge.points;

      // Check for level up BEFORE updating state
      checkLevelUp(newScore);

      setStreak(newStreak);
      setSendItScore(newScore);

      storage.setStreak(newStreak);
      storage.setScore(newScore);

      // Update longest streak if current streak is higher
      if (newStreak > longestStreak) {
        setLongestStreak(newStreak);
        storage.setLongestStreak(newStreak);
      }

      // Add to pending outcomes
      const newPending = [...pendingOutcomes, newLog.id];
      setPendingOutcomes(newPending);
      storage.setPendingOutcomes(newPending);

      // Increment completion count
      incrementChallengeCount('weekly', weeklyChallenge.text);
    }

    // Calculate days until next weekly
    const now = Date.now();
    const weekStart = weeklyStartDate || now;
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    const timeElapsed = now - weekStart;
    const daysLeft = Math.ceil((oneWeek - timeElapsed) / (24 * 60 * 60 * 1000));
    setDaysUntilNextWeekly(Math.max(1, daysLeft)); // At least 1 day
  };

  const completeChallenge = (success) => {
    const basePoints = hardModeActive && hardModeChallenge
      ? hardModeChallenge.points
      : dailyChallenge.points;
    const pointsMultiplier = (success && recoveryMode) ? 2 : 1;
    const earnedPoints = basePoints * pointsMultiplier;

    if (success) {
      console.log('Daily completed - triggering celebration', recoveryMode ? 'recovery' : 'daily');
      playSound(recoveryMode ? 'recovery' : 'success');
      setCelebrationType(recoveryMode ? 'recovery' : 'daily');
      setShowConfetti(true);
      setPulseScore(true);
      setTimeout(() => {
        setShowConfetti(false);
        setCelebrationType(null);
      }, 3000);
      setTimeout(() => {
        setPulseScore(false);
      }, 2400);

      // Show instant share prompt
      setShareData({
        type: 'challenge',
        challenge: dailyChallenge.text,
        points: earnedPoints,
        isRecovery: recoveryMode
      });
      setShowInstantShare(true);
    } else {
      console.log('Daily bitched - triggering shake');
      playSound('bitchout');
      setShakeDaily(true);
      setTimeout(() => setShakeDaily(false), 600);

      // Check if this breaks the streak
      if (streak > 0) {
        // Show brutal streak death screen
        setShowStreakDeath(true);
        playSound('bitchout');
        setTimeout(() => {
          setShowStreakDeath(false);
          // Start undo timer for "bitched out" actions - store ALL previous state
          startUndoTimer({
            type: 'daily',
            previousLogs: logs,
            previousStreak: streak,
            previousScore: sendItScore,
            previousRecoveryMode: recoveryMode
          });
        }, 4000);
      }
    }

    const newLog = {
      id: Date.now(),
      challenge: hardModeActive && hardModeChallenge ? hardModeChallenge.text : dailyChallenge.text,
      points: basePoints,
      earnedPoints: success ? earnedPoints : 0,
      recoveryBonus: recoveryMode && success,
      isHardMode: hardModeActive && !!hardModeChallenge,
      category: dailyChallenge.category,
      success,
      outcome: null,
      timestamp: Date.now(),
      date: new Date().toLocaleDateString()
    };

    const newLogs = [newLog, ...logs];
    setLogs(newLogs);
    storage.setLogs(newLogs);

    const completedChallenge = { ...dailyChallenge, completed: true, success };
    setDailyChallenge(completedChallenge);
    storage.setDailyChallenge({
      challenge: completedChallenge,
      date: new Date()
    });

    if (success) {
      const newStreak = streak + 1;
      const newScore = sendItScore + earnedPoints;

      // Check for level up BEFORE updating state
      checkLevelUp(newScore);

      setStreak(newStreak);
      setSendItScore(newScore);
      storage.setStreak(newStreak);
      storage.setScore(newScore);
      storage.setLastCompletion(Date.now());
      setLastCompletionAt(Date.now());

      // Update longest streak if current streak is higher
      if (newStreak > longestStreak) {
        setLongestStreak(newStreak);
        storage.setLongestStreak(newStreak);
      }

      if (recoveryMode) {
        setRecoveryMode(false);
        storage.setRecoveryMode(false);
      }

      const newPending = [...pendingOutcomes, newLog.id];
      setPendingOutcomes(newPending);
      storage.setPendingOutcomes(newPending);

      // Increment completion count
      incrementChallengeCount('daily', dailyChallenge.text);

      // Challenge Lab: record community challenge completion + badge checks
      if (dailyChallenge.isCommunityPick && dailyChallenge.submissionId && userId) {
        labService.recordCompletion(dailyChallenge.submissionId)
          .then(({ isLegend, submitterId }) => {
            // If this user is the submitter and hit 100, award legend badge
            if (isLegend && submitterId === userId && !badges.includes('legend-maker')) {
              unlockBadge('legend-maker');
            }
          })
          .catch(() => {});
      }

      // Hard Mode badge + reset
      if (hardModeActive && hardModeChallenge) {
        if (!badges.includes('hard-sender')) unlockBadge('hard-sender');
        setHardModeActive(false);
        setHardModeChallenge(null);
      }

      // Show "streak safe" banner
      setShowStreakSafe(true);
      setTimeout(() => setShowStreakSafe(false), 8000);

      // Check if this is first successful send - trigger PWA prompt
      const totalSuccessfulSends = newLogs.filter(l => l.success && !l.autoBitched).length;
      if (totalSuccessfulSends === 1 && deferredPrompt) {
        const hasSeenPWAPrompt = storage.getPWAPromptSeen();
        if (!hasSeenPWAPrompt) {
          setTimeout(() => {
            if (!showInstantShare) setShowPWAPrompt(true);
          }, 4000);
        }
      } else {
        // Break streak
        if (streak > 0) {
          setStreak(0);
          setRecoveryMode(true);
          storage.setStreak(0);
          storage.setRecoveryMode(true);
        }
      }
    }
  };

  const handleSwap = () => {
    if (storage.isSwapUsedThisWeek()) {
      setSwapUsed(true);
      return;
    }

    // Mark swap as used
    storage.setSwapUsedWeek();
    setSwapUsed(true);

    // Log the swap (local only, no streak impact)
    const swapLog = {
      id: `swap_${Date.now()}`,
      challenge: dailyChallenge.text,
      date: new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      success: null,
      swapped: true,
      points: 0,
      earnedPoints: 0,
    };
    const newLogs = [swapLog, ...logs];
    setLogs(newLogs);
    storage.setLogs(newLogs);

    // Trigger swap animation then generate new challenge
    setShowRerollAnimation(true);
    setTimeout(() => {
      generateNewDailyChallenge(promotedChallenges);
      setShowRerollAnimation(false);
    }, 800);
  };

  const handleToggleHardMode = () => {
    if (!isPremium) {
      openUpgradeModal();
      return;
    }
    if (hardModeActive) {
      setHardModeActive(false);
      setHardModeChallenge(null);
    } else {
      const category = dailyChallenge?.category || 'chaos';
      const pool = hardModeChallenges[category] || hardModeChallenges.chaos;
      const picked = pool[Math.floor(Math.random() * pool.length)];
      setHardModeChallenge(picked);
      setHardModeActive(true);
    }
  };







  const getHallOfFameWinner = () => {
    if (!communityFeed || communityFeed.length === 0) {
      return {
        text: "No community posts yet - be the first!",
        votes: 0,
        username: null
      };
    }

    // Get posts from the last 7 days
    const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const recentPosts = communityFeed.filter(p => p.timestamp >= oneWeekAgo);

    if (recentPosts.length === 0) {
      return {
        text: "No posts this week - share your send!",
        votes: 0,
        username: null
      };
    }

    // Find the post with the most votes
    const winner = recentPosts.sort((a, b) => b.votes - a.votes)[0];

    return {
      text: winner.text,
      votes: winner.votes,
      username: winner.username
    };
  };

  const testOutcome = (logId) => {
    const updatedLogs = logs.map(log =>
      log.id === logId ? { ...log, timestamp: log.timestamp - (24 * 60 * 60 * 1000) } : log
    );
    setLogs(updatedLogs);
    storage.setLogs(updatedLogs);
  };

  const testAdvanceDay = () => {
    // Advance all pending outcomes by 24 hours so they become ready
    const updatedLogs = logs.map(log => {
      if (pendingOutcomes.includes(log.id) && log.success && !log.outcome) {
        return {
          ...log,
          timestamp: log.timestamp - (24 * 60 * 60 * 1000) // Move back 24 hours
        };
      }
      return log;
    });

    setLogs(updatedLogs);
    storage.setLogs(updatedLogs);

    // Generate new daily challenge (pass community picks for 20% chance)
    generateNewDailyChallenge(promotedChallenges);

    // Update last completion date to avoid streak break
    storage.setLastCompletion(Date.now());
    setLastCompletionAt(Date.now());

    // Force update current time to trigger weekly check
    setCurrentTime(Date.now());

    // Check if we need to advance the weekly challenge
    if (weeklyStartDate) {
      const now = Date.now();
      const oneWeek = 7 * 24 * 60 * 60 * 1000;
      const timeElapsed = now - weeklyStartDate;

      if (timeElapsed >= oneWeek) {
        // Week has passed
        if (!weeklyCompleted && weeklyChallenge) {
          // Auto-bitch out on weekly
          const autoBitchLog = {
            id: Date.now(),
            challenge: weeklyChallenge.text,
            points: weeklyChallenge.points,
            earnedPoints: 0,
            recoveryBonus: false,
            category: weeklyChallenge.category || 'weekly',
            success: false,
            outcome: null,
            timestamp: now,
            date: new Date().toLocaleDateString(),
            autoBitched: true,
            isWeekly: true
          };

          const logsWithWeekly = [autoBitchLog, ...updatedLogs];
          setLogs(logsWithWeekly);
          storage.setLogs(logsWithWeekly);
        }

        // Generate new weekly challenge
        console.log('Week passed - generating new weekly challenge');
        generateNewWeeklyChallenge();
        alert('⏰ Advanced to next day! Weekly challenge expired - new challenge generated.');
      } else {
        const daysRemaining = Math.ceil((oneWeek - timeElapsed) / (24 * 60 * 60 * 1000));
        alert(`⏰ Advanced to next day! New daily challenge generated. ${daysRemaining} days left on weekly challenge.`);
      }
    } else {
      alert('⏰ Advanced to next day! New daily challenge generated. Pending outcomes are now ready for review.');
    }
  };

  const resetAllData = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log('Reset function called');
    console.log('Clearing localStorage...');

    // Clear all localStorage items
    try {
      localStorage.clear();
      console.log('localStorage.clear() executed');
    } catch (err) {
      console.error('Error clearing localStorage:', err);
    }

    console.log('Reloading page...');
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const formatTimestamp = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  if (authGateMode) {
    return (
      <AuthScreen
        mode={authGateMode}
        onModeChange={setAuthGateMode}
      />
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-safe relative" style={{ fontFamily: 'Arial Black, Arial, sans-serif', isolation: 'isolate' }}>
      {/* Streak Death Screen */}
      <StreakDeath showStreakDeath={showStreakDeath} setShowStreakDeath={setShowStreakDeath} />

      {/* Instant Share Prompt */}
      <InstantShare
        showInstantShare={showInstantShare}
        shareData={shareData}
        setShowInstantShare={setShowInstantShare}
        setShareData={setShareData}
        shareToSocial={shareToSocial}
      />

      {/* Undo Banner - MUST BE FIRST */}
      <UndoBanner
        undoTimer={undoTimer}
        undoAction={undoAction}
        undoCountdown={undoCountdown}
        executeUndo={executeUndo}
        cancelUndo={cancelUndo}
      />


      {/* Onboarding Modal */}
      <OnboardingModal
        showOnboarding={showOnboarding}
        onboardingCard={onboardingCard}
        setOnboardingCard={setOnboardingCard}
        setShowOnboarding={setShowOnboarding}
        username={username}
        setShowUsernameSetup={setShowUsernameSetup}
      />

      {/* Username Setup Modal */}
      <UsernameSetup
        showUsernameSetup={showUsernameSetup}
        showEditProfile={showEditProfile}
        setShowEditProfile={setShowEditProfile}
        setShowUsernameSetup={setShowUsernameSetup}
        usernameInput={usernameInput}
        setUsernameInput={setUsernameInput}
        usernameError={usernameError}
        setUsernameError={setUsernameError}
        MAX_USERNAME_LENGTH={MAX_USERNAME_LENGTH}
        MIN_USERNAME_LENGTH={MIN_USERNAME_LENGTH}
        canChangeUsername={canChangeUsername}
        bioInput={bioInput}
        setBioInput={setBioInput}
        MAX_BIO_LENGTH={MAX_BIO_LENGTH}
        saveUsername={saveUsername}
        username={username}
      />

      {/* Profile View Modal */}
      <ViewProfileModal
        showProfile={showProfile}
        viewingProfile={viewingProfile}
        setShowProfile={setShowProfile}
        setViewingProfile={setViewingProfile}
        formatTimestamp={formatTimestamp}
      />

      {/* Share Success Toast */}
      <ShareSuccessToast showShareSuccess={showShareSuccess} />

      {/* Streak Safe Banner */}
      <StreakSafeBanner showStreakSafe={showStreakSafe} setShowStreakSafe={setShowStreakSafe} />

      {/* PWA Install Prompt */}
      <PWAInstallPrompt
        showPWAPrompt={showPWAPrompt}
        deferredPrompt={deferredPrompt}
        setShowPWAPrompt={setShowPWAPrompt}
        setDeferredPrompt={setDeferredPrompt}
      />

      {/* Streak Warning Banner */}
      <StreakWarningBanner
        showStreakWarning={showStreakWarning}
        getStreakWarningStatus={getStreakWarningStatus}
        streak={streak}
        setShowStreakWarning={setShowStreakWarning}
        setCurrentView={setCurrentView}
      />


      {/* Badge Unlock Effect */}
      <BadgeUnlockEffect showBadgeUnlock={showBadgeUnlock} newlyUnlockedBadge={newlyUnlockedBadge} />

      {/* Level Up Effect */}
      <LevelUpEffect showLevelUp={showLevelUp} levelUpData={levelUpData} />

      {/* Glitch/Celebration Effect */}
      <CelebrationEffect showConfetti={showConfetti} celebrationType={celebrationType} />



      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Header
          sendItScore={sendItScore}
          animatedStreak={animatedStreak}
          animatedScore={animatedScore}
          longestStreak={longestStreak}
          pulseScore={pulseScore}
        />

        {/* Navigation */}
        <NavBar currentView={currentView} setCurrentView={(view) => {
          setCurrentView(view);
          if (view === 'community') loadCommunityFeed();
          if (view === 'pact') setPactNotification(false);
          if (view !== 'home') setChallengeSubView('challenges');
        }} pactNotification={pactNotification} />

        {/* Home View - Challenges */}
        {currentView === 'home' && dailyChallenge && (
          <HomeView
            dailyChallenge={dailyChallenge}
            shakeWeekly={shakeWeekly}
            weeklyChallenge={weeklyChallenge}
            weeklyCompleted={weeklyCompleted}
            getTimeRemaining={getTimeRemaining}
            weeklyChallengeCount={weeklyChallengeCount}
            completeWeeklyChallenge={completeWeeklyChallenge}
            daysUntilNextWeekly={daysUntilNextWeekly}
            recoveryMode={recoveryMode}
            shakeDaily={shakeDaily}
            showRerollAnimation={showRerollAnimation}
            dailyChallengeCount={dailyChallengeCount}
            completeChallenge={completeChallenge}
            shareToSocial={shareToSocial}
            challengeSubView={challengeSubView}
            setChallengeSubView={setChallengeSubView}
            userId={userId}
            username={username}
            isPremium={isPremium}
            openUpgradeModal={openUpgradeModal}
            swapUsed={swapUsed}
            onSwap={handleSwap}
            hardModeActive={hardModeActive}
            hardModeChallenge={hardModeChallenge}
            onToggleHardMode={handleToggleHardMode}
          />
        )}

        {/* Decision Roulette */}
        {currentView === 'roulette' && (
          <ExecuteMode
            coinFlipMode={coinFlipMode}
            setCoinFlipMode={setCoinFlipMode}
            showResult={showResult}
            setShowResult={setShowResult}
            coinResult={coinResult}
            setCoinResult={setCoinResult}
            decisionText={decisionText}
            setDecisionText={setDecisionText}
            optionA={optionA}
            setOptionA={setOptionA}
            optionB={optionB}
            setOptionB={setOptionB}
            countdown={countdown}
            startDecisionCountdown={startDecisionCountdown}
            savedDecisions={savedDecisions}
            isPremium={isPremium}
            openUpgradeModal={openUpgradeModal}
          />
        )}

        {/* Log View */}
        {currentView === 'log' && (
          <LogView
            sendItScore={sendItScore}
            longestStreak={longestStreak}
            streak={streak}
            logs={logs}
            pendingOutcomes={pendingOutcomes}
            testOutcome={testOutcome}
          />
        )}

        {/* Community Feed */}
        {currentView === 'community' && (
          <CommunityView
            communitySubView={communitySubView}
            setCommunitySubView={setCommunitySubView}
            getUserStats={getUserStats}
            MIN_SENDS_TO_POST={MIN_SENDS_TO_POST}
            shareText={shareText}
            setShareText={setShareText}
            MAX_POST_LENGTH={MAX_POST_LENGTH}
            isPosting={isPosting}
            postError={postError}
            shareCategory={shareCategory}
            setShareCategory={setShareCategory}
            shareVibe={shareVibe}
            setShareVibe={setShareVibe}
            wouldSendAgain={wouldSendAgain}
            setWouldSendAgain={setWouldSendAgain}
            addToFeed={addToFeed}
            feedFilter={feedFilter}
            setFeedFilter={setFeedFilter}
            showSendAgainOnly={showSendAgainOnly}
            setShowSendAgainOnly={setShowSendAgainOnly}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            isLoadingFeed={isLoadingFeed}
            getFilteredFeed={getFilteredFeed}
            userId={userId}
            userVotes={userVotes}
            activeCommentPost={activeCommentPost}
            setActiveCommentPost={setActiveCommentPost}
            comments={comments}
            commentText={commentText}
            setCommentText={setCommentText}
            commentCooldown={commentCooldown}
            isPostingComment={isPostingComment}
            addComment={addComment}
            upvotePost={upvotePost}
            flagPost={flagPost}
            deletePost={deletePost}
            flagComment={flagComment}
            deleteComment={deleteComment}
            shareToSocial={shareToSocial}
            formatTimestamp={formatTimestamp}
            setViewingProfile={setViewingProfile}
            setShowProfile={setShowProfile}
            getUserCategoryBreakdown={getUserCategoryBreakdown}
            getUserRecentSends={getUserRecentSends}
            AUTO_HIDE_FLAG_THRESHOLD={AUTO_HIDE_FLAG_THRESHOLD}
            MAX_COMMENT_LENGTH={MAX_COMMENT_LENGTH}
            MIN_COMMENT_LENGTH={MIN_COMMENT_LENGTH}
            isPremium={isPremium}
            openUpgradeModal={openUpgradeModal}
          />
        )}

        {/* Send It Pact */}
        {currentView === 'pact' && (
          <PactView
            userId={userId}
            username={username}
            dailyChallengeCompleted={!!(dailyChallenge?.completed && dailyChallenge?.success)}
            onUnlockBadge={unlockBadge}
            playSound={playSound}
            sendNotificationDirect={sendNotificationDirect}
            pendingPactCode={pendingPactCode}
          />
        )}

        {/* Profile View */}
        {currentView === 'profile' && (
          <ProfileView
            isDataLoaded={isDataLoaded}
            username={username}
            bio={bio}
            userId={userId}
            badges={badges}
            BADGE_DEFINITIONS={BADGE_DEFINITIONS}
            getUserStats={getUserStats}
            sendItScore={sendItScore}
            getRecentSends={getRecentSends}
            setUsernameInput={setUsernameInput}
            setBioInput={setBioInput}
            setShowEditProfile={setShowEditProfile}
            setShowUsernameSetup={setShowUsernameSetup}
            shareToSocial={shareToSocial}
            soundEnabled={soundEnabled}
            setSoundEnabled={setSoundEnabled}
            playSound={playSound}
            notificationPermission={notificationPermission}
            requestNotificationPermission={requestNotificationPermission}
            isDev={isDev}
            testAdvanceDay={testAdvanceDay}
            resetAllData={resetAllData}
            isPremium={isPremium}
            openUpgradeModal={openUpgradeModal}
            isAnonymousUser={isAnonymousUser}
            hasCloudSession={hasCloudSession}
            authEmail={authEmail}
            authEmailConfirmed={authEmailConfirmed}
            premiumAccountPrompt={premiumAccountPrompt}
            linkAnonymousAccount={linkAnonymousAccount}
          />
        )}
      </div>

      {/* Premium upgrade modal — rendered at root so it overlays everything */}
      <PremiumUpgradeModal
        isOpen={showUpgradeModal}
        accountEmail={authEmail}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};



export default SendItMaxxApp;